import React, { useEffect, useRef, useState } from "react";

import AddCircle from "@mui/icons-material/AddCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    Autocomplete,
    Box,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from "@mui/material";

import { ButtonVariant } from "Constant/button";

import ScrollableContainer from "components/atoms/ScrollableContainer";
import StandartButton from "components/atoms/StandartButton";
import {
    StatusDotSmall,
    StatusPill,
} from "components/organisms/GenericTable/GenericTable.styled";

import { DynamicFieldProps } from "./DynamicFieldTable.types";

const DynamicFieldTable = ({
    fields,
    setFields,
    fieldsConfiguration,
    onFieldsChange,
    minLength,
    addMoreDisabled,
    addMoreTooltip,
    maxHeight = "50dvh",
    showStatus = true,
    isScrollable = true,
}: DynamicFieldProps) => {
    const containerRef = useRef(null);
    const [scrollBottom, setScrollBottom] = useState(false);
    const handleAddNewFields = () => {
        setFields([
            ...fields,
            fieldsConfiguration.reduce((acc, cur) => {
                acc[cur.name] = { value: cur.defaultValue ?? null };
                return acc;
            }, {}),
        ]);
        setScrollBottom(true);
    };
    const handleDeleteRow = (rowIndex) => {
        setFields(fields?.filter((_, index) => index !== rowIndex));
    };

    const handleFieldChange = ({ rowIndex, name, value }) => {
        onFieldsChange &&
            onFieldsChange({
                rowIndex,
                name,
                value,
            });
        setFields(
            fields?.map((field, index) => {
                if (rowIndex === index) {
                    field[name].value = value;
                }
                return field;
            }),
        );
    };

    useEffect(() => {
        if (!scrollBottom) return;

        const el = containerRef.current;
        if (el) {
            requestAnimationFrame(() => {
                el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            });
        }
        setScrollBottom(false);
    }, [scrollBottom, fields]);

    const renderFieldInput = (
        row,
        rowIndex,
        {
            name,
            variant,
            placeholder,
        }: {
            name: string;
            variant: any;
            placeholder?: string;
        },
    ) => {
        const rowVariant = row[name]?.variant || variant;

        if (rowVariant === "FREESOLO_SELECT" || rowVariant === "AUTOCOMPLETE") {
            return (
                <Autocomplete
                    freeSolo={rowVariant === "FREESOLO_SELECT"}
                    options={row[name]?.options || []}
                    getOptionLabel={(option) => {
                        if (typeof option === "string") {
                            return option;
                        }
                        return option.label || "";
                    }}
                    value={
                        row[name]?.options?.find(
                            (opt) => opt.value === row[name]?.value,
                        ) ||
                        row[name]?.value ||
                        null
                    }
                    onChange={(event, newValue) => {
                        let valueToSet = "";
                        if (newValue && typeof newValue === "object") {
                            valueToSet = newValue.value;
                        } else if (typeof newValue === "string") {
                            valueToSet = newValue;
                        }
                        handleFieldChange({
                            rowIndex,
                            name,
                            value: valueToSet,
                        });
                    }}
                    {...(rowVariant === "FREESOLO_SELECT"
                        ? {
                              onInputChange: (event, newInputValue) => {
                                  if (event?.type === "change") {
                                      handleFieldChange({
                                          rowIndex,
                                          name,
                                          value: newInputValue,
                                      });
                                  }
                              },
                          }
                        : {})}
                    disabled={row?.disabled}
                    size="small"
                    fullWidth
                    renderOption={(
                        props,
                        option: { label?: string } | string,
                    ) => {
                        const { key, ...optionProps } = props;
                        const label =
                            typeof option === "string"
                                ? option
                                : option.label || "";

                        return (
                            <Box
                                key={key}
                                component="li"
                                {...optionProps}
                                sx={{
                                    "&&": {
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    },
                                }}
                            >
                                {label}
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={
                                row[name]?.placeholder ||
                                placeholder ||
                                "Select or enter custom value"
                            }
                            className="form-control-bg-white"
                            size="small"
                        />
                    )}
                />
            );
        }

        if (rowVariant === "SELECT") {
            return (
                <Select
                    value={row[name]?.value ?? ""}
                    onChange={(e) =>
                        handleFieldChange({
                            rowIndex,
                            name,
                            value: e?.target?.value,
                        })
                    }
                    size="small"
                    fullWidth
                    disabled={row?.disabled || !row[name]?.options?.length}
                    displayEmpty
                    className="form-control-bg-white"
                    renderValue={(selected) => {
                        if (!selected) return "";
                        const selectedOption = row[name]?.options?.find(
                            (option) => option.value === selected,
                        );
                        return selectedOption?.label || selected;
                    }}
                >
                    {row[name]?.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            );
        }

        if (variant === "SWITCH") {
            return (
                <Switch
                    checked={
                        row[name]?.value === true || row[name]?.value === "true"
                    }
                    onChange={(e) =>
                        handleFieldChange({
                            rowIndex,
                            name,
                            value: e.target.checked,
                        })
                    }
                    disabled={row?.disabled}
                    size="small"
                />
            );
        }

        return (
            <TextField
                value={row[name]?.value ?? ""}
                size="small"
                fullWidth
                disabled={row?.disabled}
                autoFocus={row[name]?.autoFocus}
                placeholder={row[name]?.placeholder || placeholder}
                className="form-control-bg-white"
                onChange={(e) =>
                    handleFieldChange({
                        rowIndex,
                        name,
                        value: e?.target?.value,
                    })
                }
                onKeyDown={(e) => {
                    if (rowVariant === "TEXT" && e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            />
        );
    };

    const tableContent = (
        <TableContainer
            style={{
                background: "#F8F8F8",
                overflow: "visible",
            }}
        >
            <Table stickyHeader sx={{ tableLayout: "fixed" }}>
                            <TableHead>
                                <TableRow>
                                    {fieldsConfiguration?.map(
                                        ({ label, width, variant }) => (
                                            <TableCell
                                                align={
                                                    variant === "SWITCH"
                                                        ? "center"
                                                        : "left"
                                                }
                                                key={label}
                                                width={width}
                                            >
                                                {label}
                                            </TableCell>
                                        ),
                                    )}
                                    {showStatus && (
                                        <TableCell align="left" width="150px">
                                            Status
                                        </TableCell>
                                    )}
                                    <TableCell align="right" width="100px">
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody
                                style={{
                                    overflow: "hidden",
                                }}
                            >
                                {fields?.map((row, rowIndex) => (
                                    <TableRow
                                        key={rowIndex}
                                        sx={{
                                            "&:last-child td, &:last-child th":
                                                {
                                                    border: 0,
                                                },
                                        }}
                                    >
                                        {fieldsConfiguration?.map(
                                            (fieldConfig) => (
                                                <TableCell
                                                    key={fieldConfig.name}
                                                    width={fieldConfig.width}
                                                    align={
                                                        fieldConfig.variant ===
                                                        "SWITCH"
                                                            ? "center"
                                                            : "left"
                                                    }
                                                >
                                                    {renderFieldInput(
                                                        row,
                                                        rowIndex,
                                                        fieldConfig,
                                                    )}
                                                </TableCell>
                                            ),
                                        )}
                                        {showStatus && (
                                            <TableCell
                                                align="left"
                                                width="150px"
                                            >
                                                {row?.disabled ? (
                                                    <StatusPill $status="idle">
                                                        <StatusDotSmall />
                                                        Not Editable
                                                    </StatusPill>
                                                ) : (
                                                    <StatusPill $status="healthy">
                                                        <StatusDotSmall />
                                                        Editable
                                                    </StatusPill>
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell align="right" width="100px">
                                            <StandartButton
                                                iconButton
                                                variant={ButtonVariant.DANGER}
                                                disabled={
                                                    row?.disabled ||
                                                    minLength === fields?.length
                                                }
                                                onClick={() =>
                                                    handleDeleteRow(rowIndex)
                                                }
                                            >
                                                <DeleteOutlineIcon />
                                            </StandartButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
        </TableContainer>
    );

    return (
        <Stack width="100%">
            <Paper
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    background: "#F8F8F8",
                }}
                elevation={0}
            >
                {isScrollable ? (
                    <ScrollableContainer
                        maxHeight={maxHeight}
                        ref={containerRef}
                    >
                        {tableContent}
                    </ScrollableContainer>
                ) : (
                    <>{tableContent}</>
                )}
                <Box
                    padding="4px 12px"
                    marginBottom="12px"
                    marginTop={"12px"}
                    display="flex"
                    alignItems="center"
                    gap={1}
                >
                    <Tooltip title={addMoreDisabled ? addMoreTooltip : ""}>
                        <Box sx={{ width: "fit-content" }}>
                            <StandartButton
                                variant={ButtonVariant.BLACK_OUTLINED}
                                iconButton
                                onClick={handleAddNewFields}
                                disabled={addMoreDisabled}
                            >
                                <Stack direction="row" gap={1}>
                                    <AddCircle />
                                    <Box fontSize="14px" fontWeight={600}>
                                        ADD ROW{" "}
                                    </Box>
                                </Stack>
                            </StandartButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Paper>
        </Stack>
    );
};

export default DynamicFieldTable;
